// src/components/Footer.jsx

import React from "react";
import { Link } from "react-router-dom";
import {
  FaInstagram,
  FaWhatsapp,
  FaEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaArrowRight,
  FaCode,
  FaMobileAlt,
  FaBullhorn,
  FaVideo,
  FaUsers,
} from "react-icons/fa";

import logo from "../assets/primeappconnect-logo-mark.png";

const quickLinks = [
  ["/", "Home"],
  ["/services", "Services"],
  ["/about", "About Us"],
  ["/contact", "Contact"],
];

const serviceLinks = [
  {
    label: "Web Development",
    icon: <FaCode />,
  },
  {
    label: "Mobile App Development",
    icon: <FaMobileAlt />,
  },
  {
    label: "Digital Marketing",
    icon: <FaBullhorn />,
  },
  {
    label: "Video & AI Services",
    icon: <FaVideo />,
  },
  {
    label: "CRM Development",
    icon: <FaUsers />,
  },
];

const policies = [
  ["/privacy", "Privacy Policy"],
  ["/terms", "Terms & Conditions"],
  ["/refund", "Refund Policy"],
];

export default function Footer() {
  return (
    <footer className="relative overflow-hidden bg-[#06152f] font-[Manrope] text-white">

      {/* Background */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-32 top-10 h-72 w-72 rounded-full bg-blue-500/15 blur-3xl" />

        <div className="absolute -right-32 bottom-0 h-80 w-80 rounded-full bg-cyan-400/10 blur-3xl" />

        <div
          className="absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />

        <div className="absolute left-1/2 top-0 h-px w-2/3 -translate-x-1/2 bg-gradient-to-r from-transparent via-blue-400/40 to-transparent" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 py-12 sm:px-8 lg:px-10">

        {/* Main Footer */}
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-[1.45fr_0.7fr_1.1fr_1.1fr]">

          {/* Brand */}
          <div>
            <Link
              to="/"
              className="group inline-flex items-center gap-3"
            >
              <div className="relative flex h-11 w-11 items-center justify-center rounded-xl bg-white shadow-lg shadow-blue-900/20 transition duration-300 group-hover:-translate-y-0.5">
                <img
                  src={logo}
                  alt="PrimeAppConnect"
                  className="h-8 w-8 object-contain transition duration-300 group-hover:scale-105"
                />
              </div>

              <div>
                <div className="text-lg font-extrabold tracking-tight">
                  <span className="text-white">Prime</span>
                  <span className="text-cyan-300">App</span>
                  <span className="text-blue-400">Connect</span>
                </div>

                <p className="mt-0.5 text-[8px] font-bold tracking-[0.3em] text-blue-200/50">
                  DIGITAL SOLUTIONS
                </p>
              </div>
            </Link>

            <p className="mt-5 max-w-sm text-sm leading-6 text-blue-100/55">
              We build modern websites, mobile apps, CRM systems,
              marketing solutions and AI-powered digital products
              that help businesses grow.
            </p>

            {/* Socials */}
            <div className="mt-6 flex gap-2.5">
              <SocialLink
                href="https://www.instagram.com/primeapp.connect_"
                label="Instagram"
                icon={<FaInstagram />}
              />

              <SocialLink
                href="mailto:primeappconnect@gmail.com"
                label="Email"
                icon={<FaEnvelope />}
              />

              <SocialLink
                href="https://wa.me/916301898060"
                label="WhatsApp"
                icon={<FaWhatsapp />}
                whatsapp
              />
            </div>
          </div>

          {/* Company */}
          <FooterColumn title="Company">
            {quickLinks.map(([to, label]) => (
              <FooterLink key={label} to={to}>
                {label}
              </FooterLink>
            ))}
          </FooterColumn>

          {/* Services */}
          <FooterColumn title="Services">
            <div className="space-y-3">
              {serviceLinks.map((service) => (
                <Link
                  key={service.label}
                  to="/services"
                  className="group flex items-center gap-2.5 text-xs text-blue-100/55 transition duration-200 hover:text-cyan-300"
                >
                  <span className="text-[10px] text-blue-400 transition group-hover:text-cyan-300">
                    {service.icon}
                  </span>

                  <span>{service.label}</span>
                </Link>
              ))}
            </div>
          </FooterColumn>

          {/* Contact */}
          <div>
            <h3 className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-white">
              Get In Touch
            </h3>

            <div className="mt-5 space-y-4">

              <ContactItem
                href="tel:+916301898060"
                icon={<FaPhoneAlt />}
                label="Phone"
              >
                +91 63018 98060
              </ContactItem>

              <ContactItem
                href="mailto:primeappconnect@gmail.com"
                icon={<FaEnvelope />}
                label="Email"
              >
                primeappconnect@gmail.com
              </ContactItem>

              <ContactItem
                href="https://maps.app.goo.gl/UdfLXDpf4Nyc2B699"
                icon={<FaMapMarkerAlt />}
                label="Location"
                external
              >
                13.559823, 78.503698
              </ContactItem>

            </div>

            <Link
              to="/contact"
              className="group mt-6 inline-flex items-center gap-2 rounded-lg bg-white/5 px-3.5 py-2 text-xs font-bold text-cyan-300 ring-1 ring-white/10 transition duration-300 hover:-translate-y-0.5 hover:bg-cyan-400 hover:text-[#06152f]"
            >
              Let's Talk

              <FaArrowRight className="text-[9px] transition duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-10 border-t border-white/10 pt-5">

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">

            <p className="text-[10px] text-blue-100/35">
              © {new Date().getFullYear()} PrimeAppConnect. All rights reserved.
            </p>

            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 sm:justify-end">
              {policies.map(([to, label]) => (
                <Link
                  key={label}
                  to={to}
                  className="text-[10px] text-blue-100/35 transition hover:text-cyan-300"
                >
                  {label}
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-5 flex items-center justify-center gap-3">
            <span className="h-px w-8 bg-white/10" />

            <span className="text-[8px] font-bold tracking-[0.3em] text-blue-100/20">
              BUILD • GROW • SCALE
            </span>

            <span className="h-px w-8 bg-white/10" />
          </div>

        </div>
      </div>
    </footer>
  );
}

/* ============================================================
   FOOTER COLUMN
============================================================ */

function FooterColumn({ title, children }) {
  return (
    <div>
      <h3 className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-white">
        {title}
      </h3>

      <div className="mt-5 space-y-3">
        {children}
      </div>
    </div>
  );
}

/* ============================================================
   FOOTER LINK
============================================================ */

function FooterLink({ to, children }) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-2 text-xs text-blue-100/55 transition duration-200 hover:text-cyan-300"
    >
      <FaArrowRight className="text-[7px] text-blue-400 opacity-0 transition duration-200 group-hover:translate-x-1 group-hover:opacity-100" />

      <span className="transition duration-200 group-hover:translate-x-0.5">
        {children}
      </span>
    </Link>
  );
}

/* ============================================================
   CONTACT ITEM
============================================================ */

function ContactItem({
  href,
  icon,
  label,
  children,
  external = false,
}) {
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className="group flex items-start gap-3"
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-[10px] text-blue-300 transition duration-200 group-hover:border-cyan-300/20 group-hover:bg-cyan-300/10 group-hover:text-cyan-300">
        {icon}
      </span>

      <div className="min-w-0">
        <p className="text-[8px] font-bold uppercase tracking-[0.14em] text-blue-100/30">
          {label}
        </p>

        <p className="mt-1 break-all text-xs leading-5 text-blue-100/60 transition group-hover:text-cyan-300">
          {children}
        </p>
      </div>
    </a>
  );
}

/* ============================================================
   SOCIAL LINK
============================================================ */

function SocialLink({
  href,
  label,
  icon,
  whatsapp = false,
}) {
  const isExternal = href.startsWith("http");

  return (
    <a
      href={href}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}
      aria-label={label}
      className={`flex h-9 w-9 items-center justify-center rounded-lg border bg-white/5 text-sm transition duration-300 hover:-translate-y-1 ${
        whatsapp
          ? "border-white/10 text-blue-100/45 hover:border-emerald-400/30 hover:bg-emerald-400/10 hover:text-emerald-400"
          : "border-white/10 text-blue-100/45 hover:border-cyan-400/30 hover:bg-cyan-400/10 hover:text-cyan-300"
      }`}
    >
      {icon}
    </a>
  );
}